<template>
  <base-layout>
    <base-dashboard
      :is-loading="isLoading"
      id="EditCampaign"
    >
      <div
        class="page-header bg-secondary m-0 p-3 border-bottom"
      >
        <p
          class="page-title mr-4 ellipsis mb-0"
          v-b-tooltip.hover.top.ds800
          :title="campaign.name"
        >
          Editing - {{ campaign.name }}
        </p>
        <p :class="['label px-2 mb-0', getClassForLabel]">
          {{ campaign.state }}
        </p>
        <div class="page-options d-flex">
          <b-btn
            v-show="shouldShowCampaignShareButton"
            :disabled="!this.campaign.id"
            class="btn btn-secondary mr-3"
            data-test="campaign-share-button"
            @click="$refs['modalSharingCampaign'].openModal()"
            v-b-tooltip.hover
            title="Share a campaign with third parties"
          >
            Share Campaign
            <i class="fal fa-share-square ml-2" />
          </b-btn>
          <b-btn
            v-if="tabIndex===0"
            variant="primary"
            class="ml-2 border-0"
            data-test="save-changes-button"
            @click="applyChanges"
            :disabled="isSaveButtonDisabled"
            :class="{ 'btn-loading': isButtonLoading }"
          >
            Save changes
          </b-btn>
          <b-btn
            v-show="tabIndex === 2 && (!this.campaign.id || this.GET_HAS_CAMPAIGN_ACCESS)"
            :disabled="this.GET_IS_CAMPAIGN_READONLY || this.sharedCount < 1"
            class="btn share-btn btn-secondary mr-3"
            data-test="showcase-share-button"
            @click="$refs['modalSharingShowcase'].openModal()"
            v-b-tooltip.hover
            title="Share a showcase of your creative and content with third parties"
          >
            Share Preview
            <i class="fal fa-share-square ml-2" />
          </b-btn>
        </div>
      </div>
      <b-tabs

        id="EditCampaign"
        class="campaign-edit-nav d-flex flex-column"
        nav-wrapper-class="bg-white border-bottom panel-nav"
        nav-class="px-3"
        content-class="flex-grow-1 w-100 overflow-hidden"
        v-model="tabIndex"
        lazy
      >
        <b-tab
          class="overflow-auto"
        >
          <template v-slot:title>
            <i class="fa-light fa-pen-alt" /> Basic Info
          </template>
          <b-row
            class="m-0"
          >
            <b-col
              class="p-5"
              cols="auto"
              lg="8"
            >
              <campaign-info
                @updateOrderDetails="updateOrderDetails"
                @setNotValidForNavigation="updateIsNotValidForNavigation"
                @next-step="nextStep"
                @setIsDirty="setIsDirty"
                ref="campaign-info"
              />
            </b-col>
            <b-col
              cols="4"
              class="p-5"
            >
              <campaign-details-card
                ref="CampaignDetailsCard"
              />
            </b-col>
          </b-row>
        </b-tab>
        <b-tab
          class="h-100 overflow-auto"
          title-link-class="edit-campaign-landing-pages-tab"
        >
          <template v-slot:title>
            <i class="fal fa-list-alt" /> Landing Pages
          </template>
          <new-content-info
            ref="new-content-info"
            @setIsValidForNavigation="setIsValidForNavigation"
          />
        </b-tab>
        <b-tab
          class="overflow-auto"
          title-link-class="edit-campaign-creative-tab"
        >
          <template v-slot:title>
            <i class="fal fa-paintbrush-pencil" /> Creative
          </template>
          <creatives-page
            class="h-100 w-100"
            ref="creatives-page"
          />
        </b-tab>
        <b-tab
          class="overflow-auto"
        >
          <template v-slot:title>
            <i class="fa-light fa-coins" /> Billing
          </template>
          <campaign-invoicing
            :edit-mode="true"
          />
        </b-tab>
      </b-tabs>
    </base-dashboard>
    <share-modal
      id="modalSharingCampaign"
      ref="modalSharingCampaign"
      :share-type="'CAMPAIGN'"
    />
    <share-modal
      id="modalSharingShowcase"
      ref="modalSharingShowcase"
      :share-type="'SHOWCASE'"
    />
    <campaign-changes-modal
      :is-apply-changes-button-loading="isApplyChangesButtonLoading"
      @applyChanges="applyChanges()"
      @discardChanges="discardChanges()"
      @continueEditing="isButtonLoading = false"
    />
  </base-layout>
</template>

<script>
import {
  mapActions, mapGetters, mapState, mapMutations,
} from 'vuex';
import * as types from '@/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';
import * as shareTypes from '@/modules/share/store/types';
import * as authenticationTypes from '@/modules/authentication/store/types';
import CampaignInfo from '@/modules/campaigns/components/CampaignInfo/CampaignInfo';
import NewContentInfo from '@/modules/content/components/ContentPage/ContentInfo';
import CreativesPage from '@/modules/content/components/CreativesPage/CreativesPage';
import CampaignInvoicing from '@/modules/campaigns/components/CampaignInvoicing';
import CampaignDetailsCard from '@/modules/campaigns/components/CampaignDetailsCard';
import ShareModal from '@/modules/share/components/ShareModal';

import CampaignChangesModal from '@/modules/campaigns/components/CampaignChangesModal';

import campaignMixin from '@/core/mixins/campaignHelper';
import BaseLayout from '@/components/BaseLayout.vue';
import BaseDashboard from '@/components/BaseDashboard.vue';

export default {
  name: 'EditCampaign',
  mixins: [
    campaignMixin,
  ],
  components: {
    BaseDashboard,
    BaseLayout,
    CampaignInfo,
    NewContentInfo,
    CreativesPage,
    CampaignDetailsCard,
    ShareModal,
    CampaignInvoicing,
    CampaignChangesModal,
  },
  data() {
    return {
      isLoading: true,
      isButtonLoading: false,
      isApplyChangesButtonLoading: false,
      campaignInfoKey: 0,
      tabIndex: 0,
      isValidLastButton: false,
      isValidNextButton: false,
      isDirty: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'campaignChanges',
      'order',
      'orderChanges',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_HAS_CAMPAIGN_ACCESS,
      campaignTypes.GET_CAMPAIGN_TOTAL_BUDGET,
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
      campaignTypes.GET_CAMPAIGN_PERMISSIONS,
    ]),
    ...mapState('content', [
      'activeCreatives',
      'creative',
      'sharedCount',
    ]),
    ...mapGetters('share', [
      shareTypes.GET_CAMPAIGN_SHARES,
    ]),
    ...mapGetters('authentication', [
      authenticationTypes.GET_IS_ADMIN,
    ]),
    isSaveButtonDisabled() {
      return this.GET_IS_CAMPAIGN_READONLY
      || !this.isValidLastButton
      || !this.isValidNextButton
      || !this.isDirty;
    },
    creativeKey() {
      return this.creative.paused ? this.activeCreatives[0].creativeKey : this.creative.creativeKey;
    },
    wasBudgetChanged() {
      return this.order.budget !== this.GET_CAMPAIGN_TOTAL_BUDGET;
    },
    getClassForLabel() {
      return this.getLabelClass();
    },
    shouldShowCampaignShareButton() {
      const campaignPermissions = this.GET_CAMPAIGN_PERMISSIONS.get(this.campaign.id);
      return !this.GET_IS_ADMIN && (campaignPermissions?.ownerAccess || campaignPermissions?.sharedTypes.includes('CAMPAIGN'));
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_EXISTING_CONTENT_PROVIDER,
      campaignTypes.FETCH_EXISTING_ADVERTISERS,
      campaignTypes.FETCH_CURRENCIES,
      campaignTypes.FETCH_BANNED_WORDS,
      campaignTypes.FETCH_EXISTING_CAMPAIGN,
      campaignTypes.FETCH_ORDER,
      campaignTypes.FETCH_EXISTING_BLACKLIST,
      campaignTypes.RESET_CAMPAIGN,
      campaignTypes.UPDATE_SELECTED_ADVERTISER,
      campaignTypes.SET_VIEW_TYPE,
      campaignTypes.SET_CAMPAIGN_STATE,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_EXISTING_CONTENT_SCRAPES,
      contentTypes.FETCH_FEED_CONTENT_ITEMS,
      contentTypes.FETCH_EXISTING_CREATIVES,
      contentTypes.FETCH_FILTERED_FEED_CONTENT_ITEMS,
      contentTypes.FETCH_CREATIVE_TEMPLATES,
    ]),
    ...mapMutations('content', [
      contentTypes.SET_CONTENT_LIST_TYPE,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    fetchCampaignPageEntities(campaignId) {
      const contentProviderPromise = this.FETCH_EXISTING_CONTENT_PROVIDER();
      const currenciesPromise = this.FETCH_CURRENCIES();
      const bannedWordsPromise = this.FETCH_BANNED_WORDS();
      Promise.all([contentProviderPromise, currenciesPromise, bannedWordsPromise])
        .then(() => {
          const { advertiserId } = this.$route.params;
          if (typeof campaignId !== 'undefined') {
            this.fetchExistingCampaignEntities(advertiserId, campaignId);
          } else { this.isLoading = false; }
        });
    },
    fetchExistingCampaignEntities(advertiserId, campaignId) {
      const campaignPromise = this.FETCH_EXISTING_CAMPAIGN({ advertiserId, campaignId });
      const orderPromise = this.FETCH_ORDER({ advertiserId, campaignId, setOrder: true });
      Promise.all([
        campaignPromise,
        orderPromise,
      ])
        .then(async () => {
          await this.FETCH_EXISTING_CREATIVES({ advertiserId });
          await this.FETCH_EXISTING_BLACKLIST();
          if (this.$route.params.hasOwnProperty('targetPage')) this.tabIndex = this.$route.params.targetPage;
          this.isLoading = false;
        });
    },
    updateOrderDetails(payload) {
      if ((this.$refs.CampaignDetailsCard)) {
        this.$refs.CampaignDetailsCard.setOrderDetails(payload);
      }
    },
    async handleCreate() {
      this.isLoading = true;
      const { campaignId, advertiserId } = this.$route.params;
      this.campaignId = campaignId;
      this.RESET_CAMPAIGN();
      const advertisers = await this.FETCH_EXISTING_ADVERTISERS();
      const aid = Number.parseInt(advertiserId, 10) || advertisers[0].id;
      const advertiserSet = advertisers
        .find((advertiser) => advertiser.id === aid);
      if (advertiserId && advertiserSet) {
        await this.UPDATE_SELECTED_ADVERTISER(advertiserSet);
      }
      this.fetchCampaignPageEntities(campaignId);
    },
    displayGlobalMessage() {
      if (this.wasBudgetChanged) {
        this.SET_GLOBAL_MESSAGE({
          message: 'Your changes have been sent for APPROVAL. You will receive an email when your Campaign is LIVE',
          type: 'info',
        });
        return;
      }
      this.SET_GLOBAL_MESSAGE({
        message: 'Campaign has been updated',
        type: 'info',
      });
    },
    nextStep() {
      this.isDirty = false;
    },
    updateIsNotValidForNavigation(type, valid) {
      this.setIsValidForNavigation(type, !valid);
    },
    setIsValidForNavigation(type, isValid) {
      if (type === 'last') {
        this.isValidLastButton = isValid;
      } else if (type === 'next') {
        this.isValidNextButton = isValid;
      }
    },
    setIsDirty(value) {
      this.isDirty = value;
    },
    async applyChanges() {
      this.isButtonLoading = true;
      this.isApplyChangesButtonLoading = true;
      if (this.$refs['campaign-info']) {
        this.$refs['campaign-info'].onSubmit(true)
          .then(() => {
            this.displayGlobalMessage();
            if (this.wasBudgetChanged) {
              this.FETCH_EXISTING_CAMPAIGN({
                advertiserId: this.campaign.advertiserId,
                campaignId: this.campaign.id,
              });
            }
            this.isApplyChangesButtonLoading = false;
            this.isDirty = false;
          })
          .finally(() => {
            this.isButtonLoading = false;
          });
      } else {
        this.isButtonLoading = false;
      }
    },
    discardChanges() {
      this.$refs['campaign-info'].resetPeriods();
      this.isButtonLoading = false;
    },
  },
  created() {
    this.SET_CONTENT_LIST_TYPE('EDITOR');
    this.SET_VIEW_TYPE('Editor');
    this.handleCreate();
  },
  mounted() {
    window.onbeforeunload = () => (this.isDirty ? true : null);
  },
  beforeRouteLeave(to, from, next) {
    const h = this.$createElement;
    if (!this.isDirty) return next();
    this.$bvModal.msgBoxConfirm(h('p', [
      'You have unsaved changes!',
      h('br'),
      'Press "Proceed" to continue without saving, or "Cancel" to go back and review your changes',
    ]), {
      title: 'Warning',
      size: 'sm',
      buttonSize: 'sm',
      okTitle: 'Proceed',
      okVariant: 'danger',
      cancelTitle: 'Cancel',
      cancelVariant: 'tertiary',
      footerClass: 'p-2',
      hideHeaderClose: false,
      centered: true,
    })
      .then((value) => {
        if (!value) return next(false);
        return next();
      })
      .catch(() => next(false));
  },
};
</script>
<style lang="scss" scoped>
.ellipsis {
  line-height: 2.5rem;
}
</style>
<style lang="scss">
 .campaign-edit-nav{
  //  background: #fbfbfc;
   height: calc(100% - 65px);
 }
.nav-tabs .nav-item i {
  margin: 0;
  width: 2rem;
}
.page-title {
  max-width: 70%;
}
.share-btn[disabled] {
  cursor: not-allowed !important;
  opacity: 0.65;
}
</style>
